import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader } from "../../Common/Loader";
import useSuccessErrorAlert from "../../Hooks/useSuccessErrorAlert";
import { ConfirmModel } from "../../Model/ConfirmModel";
import { ErrorModel } from "../../Model/ErrorModel";
import { SuccessModel } from "../../Model/SuccessModel";
import { setUnsavedChanges } from "../../Redux/Slice/Common/SelectedTabSlice";
import { getFeatures } from "../../Redux/Slice/Dropdowns/GetFeaturesSlice";
import { createPlanType } from "../../Redux/Slice/PlanType/CreatePlanTypeSlice";
import { getPlanTypeById } from "../../Redux/Slice/PlanType/GetPlanTypeByIdSlice";
import { updatePlanType } from "../../Redux/Slice/PlanType/UpdatePlanTypeSlice";

const AddPlanType = ({ isEdit }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const planTypeId = isEdit ? location.pathname.split("/")[2] : "";
  const { successModal, errorModal, showSuccessMessage, showErrorMessage } =
    useSuccessErrorAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [hasChangedModel, setHasChangedModel] = useState(false);
  const [formData, setFormData] = useState({
    planType: "",
    noOfAssistant: "",
    enableAdditionalAssistant: false,
    additionalNoAssistant: "",
    pricePerAssistant: "",
  });
  const [formError, setFormError] = useState({
    planType: "",
    noOfAssistant: "",
    features: "",
  });
  const [features, setFeatures] = useState([]);

  const handleChange = (e) => {
    let { value, name } = e.target;
    setHasChanged(true);
    if (name == "planType") {
      value = String(value).trimStart();
      let capitalized = value.charAt(0).toString().toUpperCase();
      value = capitalized + value.substring(1);
      if (String(value).length < 3 && String(value).length > 0) {
        setFormError((prev) => ({
          ...prev,
          planType: "Plan type name should have minimum 3 character",
        }));
      } else {
        setFormError((prev) => ({
          ...prev,
          planType: "",
        }));
      }
      setFormData({ ...formData, [name]: value });
    }

    if (
      name === "noOfAssistant" ||
      name == "additionalNoAssistant" ||
      name == "pricePerAssistant"
    ) {
      setFormData({ ...formData, [name]: parseInt(value) >= 0 ? value : "" });
    }
    dispatch(setUnsavedChanges(true));
  };

  const handleChangeFeature = (item) => {
    setHasChanged(true);
    dispatch(setUnsavedChanges(true));
    setFeatures((prev) =>
      prev?.map((feature) => {
        return {
          ...feature,
          isChecked:
            feature?.id === item?.id ? !item?.isChecked : feature?.isChecked,
        };
      })
    );
  };

  const getPlanTypeData = async () => {
    setIsLoading(true);
    try {
      let featuresList = [];
      const response = await dispatch(getFeatures());
      if (
        response?.payload?.success &&
        Array.isArray(response?.payload?.data)
      ) {
        featuresList = response?.payload?.data.map((feat) => ({
          ...feat,
          label: feat?.name,
          isChecked: false,
        }));
      }

      if (isEdit) {
        const result = await dispatch(getPlanTypeById(planTypeId));
        if (
          getPlanTypeById.fulfilled.match(result) &&
          result?.payload?.success === true
        ) {
          const planTypeData = result?.payload?.data?.response;
          setFormData((prev) => ({
            ...prev,
            planType: planTypeData?.type ?? "",
            noOfAssistant: planTypeData?.noOfAssistants ?? "",

            enableAdditionalAssistant: planTypeData?.isAdditionalAssistant,
            additionalNoAssistant: planTypeData?.additionalAssistant,
            pricePerAssistant: planTypeData?.amount
              ? parseInt(planTypeData?.amount)
              : "",
          }));
          const selectedFeatures = planTypeData?.features;
          const selectedFeaturesId = Array.isArray(selectedFeatures)
            ? selectedFeatures?.map((feature) => feature?.id)
            : [];
          featuresList = featuresList?.map((feature) => ({
            ...feature,
            isChecked: selectedFeaturesId?.includes(feature?.id),
          }));
        }
      }

      setFeatures(featuresList);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    const checkedFeatures = features
      .filter((feature) => feature?.isChecked)
      .map((feature) => feature?.id);
    let body = {
      name: formData.planType.trim(),
      features: checkedFeatures,
      noOfAssistants: parseInt(formData?.noOfAssistant),
      isAdditionalAssistant: formData.enableAdditionalAssistant,
    };
    if (formData.enableAdditionalAssistant) {
      body = {
        ...body,
        amount: parseInt(formData.pricePerAssistant),
        additionalAssistant: formData.additionalNoAssistant
          ? formData.additionalNoAssistant
          : null,
      };
    }
    dispatch(setUnsavedChanges(false));
    if (isEdit) {
      setIsLoading(true);
      const result = await dispatch(updatePlanType({ id: planTypeId, body }));
      if (
        updatePlanType.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        showSuccessMessage("Plan Type updated successfully", () =>
          navigate("/plan-types")
        );
      } else {
        setIsLoading(false);
        showErrorMessage(result?.payload?.error?.message);
      }
    } else {
      setIsLoading(true);
      const result = await dispatch(createPlanType(body));
      if (
        createPlanType.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        showSuccessMessage("Plan Type added successfully", () =>
          navigate("/plan-types")
        );
      } else {
        setIsLoading(false);
        showErrorMessage(result?.payload?.error?.message);
      }
    }
  };

  const isDisabled = useMemo(() => {
    const isFeaturesSelected = features.some((feature) => feature?.isChecked);
    const isEnableAdditionalAssistant = formData.enableAdditionalAssistant;
    const isFilledAdditionalData = isEnableAdditionalAssistant
      ? formData.additionalNoAssistant && formData.pricePerAssistant
      : true;

    return (
      !formData.planType ||
      !formData.noOfAssistant ||
      !isFeaturesSelected ||
      !isFilledAdditionalData ||
      formError.planType ||
      formError.noOfAssistant ||
      formError.features ||
      !hasChanged
    );
  }, [formData, formError, features, hasChanged]);

  useEffect(() => {
    getPlanTypeData();
  }, []);

  return (
    <div className="page__body">
      {errorModal.visible ? <ErrorModel message={errorModal.message} /> : ""}
      {successModal.visible ? (
        <SuccessModel message={successModal.message} />
      ) : (
        ""
      )}
      {isLoading ? <Loader /> : ""}
      {hasChangedModel ? (
        <ConfirmModel
          onAccept={() => {
            dispatch(setUnsavedChanges(false));
            navigate("/plan-types");
          }}
          yes={"Yes"}
          no={"No"}
          onReject={() => {
            setHasChangedModel(false);
          }}
          title="Warning!"
          message="You have unsaved changes. Are you sure you want to continue without saving?"
        />
      ) : null}
      <div className="details">
        <div className="form">
          <section className="form__section">
            <div className="formRow formRow__2">
              <div className="formCol">
                <div className="form__Field">
                  <label for="planType" className="form__label">
                    Plan Type <span style={{ color: "red" }}> * </span>
                  </label>
                  <input
                    type="text"
                    id="planType"
                    className="form__input form__input--sm"
                    name="planType"
                    maxLength={30}
                    value={formData.planType}
                    onChange={handleChange}
                    placeholder="Plan type"
                  />
                </div>
                {formError?.planType ? (
                  <div className="form__error">{formError?.planType}</div>
                ) : null}
              </div>
              <div className="formCol">
                <div className="form__Field">
                  <label for="noOfAssistant" className="form__label">
                    No. of Assistants <span style={{ color: "red" }}> * </span>
                  </label>

                  <input
                    type="number"
                    id="noOfAssistant"
                    name="noOfAssistant"
                    className="form__input form__input--sm"
                    value={formData.noOfAssistant}
                    onChange={handleChange}
                    placeholder="No. of Assistants"
                  />
                </div>
              </div>
            </div>
            <div className="formRow formRow__2">
              <div className="formCol">
                <div className="form__Field">
                  <label for="noCredits" className="form__label">
                    Enable Additional Assistants
                  </label>
                  <div className="form__switchBtn">
                    <div className="form__switchBtn__option">
                      <div className="switchBtn">
                        <input
                          type="checkbox"
                          className="toggle"
                          id="toggle-enableAdditionalAssistant"
                          checked={formData?.enableAdditionalAssistant}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              enableAdditionalAssistant: e.target.checked,
                            }));
                            setHasChanged(true);
                            dispatch(setUnsavedChanges(true));
                          }}
                        />
                        <label for="toggle-enableAdditionalAssistant">
                          <span className="on">ON</span>
                          <span className="off">OFF</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {formData.enableAdditionalAssistant ? (
              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <label for="additionalNoAssistant" className="form__label">
                      Additional No. of Assistants
                      <span style={{ color: "red" }}> * </span>
                    </label>

                    <input
                      type="number"
                      id="additionalNoAssistant"
                      name="additionalNoAssistant"
                      className="form__input form__input--sm"
                      value={formData.additionalNoAssistant}
                      onChange={handleChange}
                      placeholder="No. of Assistants"
                    />
                  </div>
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <label for="pricePerAssistant" className="form__label">
                      Price Per Assistant <span style={{ color: "red" }}> * </span>
                    </label>
                    <div className="form__groupField form__groupField--phonenumber">
                      <select
                        className="form__select form__select--sm"
                        value=""
                        disabled
                      >
                        <option value="">$</option>
                      </select>
                      <input
                        type="number"
                        id="pricePerAssistant"
                        name="pricePerAssistant"
                        className="form__input form__input--sm"
                        value={formData.pricePerAssistant}
                        onChange={handleChange}
                        placeholder="Price Per Assistant"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            <h1 className="form__section__title">Select Features</h1>
            <div className="formRow formRow__4">
              {features?.map((item) => (
                <div key={item?.id} className="form__checkboxSubField">
                  <div className="form__checkbox">
                    <div className="form__checkbox__option m_0">
                      <input
                        type="checkbox"
                        id={`feature-${item?.id}`}
                        className="form__checkbox__input"
                        value={item?.isChecked}
                        name="isDefaultEmail"
                        checked={item?.isChecked ?? false}
                        onChange={() => handleChangeFeature(item)}
                      />
                      <label
                        for={`feature-${item?.id}`}
                        className="form__checkbox__label"
                      >
                        {item?.label}
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="btn__group">
              <button
                className="btn btn__white"
                type="button"
                onClick={() => {
                  if (hasChanged) {
                    setHasChangedModel(true);
                  } else {
                    dispatch(setUnsavedChanges(false));
                    navigate("/plan-types");
                  }
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn__primary"
                type="submit"
                disabled={isDisabled}
                onClick={handleSubmit}
              >
                {isEdit ? "Update" : "Save"}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AddPlanType;
